import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  Check,
  Compass,
  Crosshair,
  Info,
  ListChecks,
  MoveHorizontal,
  Plus,
  RotateCcw,
  Route,
  Spline,
  Swords,
  Trash2,
  TriangleAlert,
  Users,
} from "lucide-react";
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { api } from "../../api/client";
import type { DeployedUnit, MeasureStyle, PhaseLineName } from "../../api/types";
import { designate, flashAttention } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { DisclosureRow, PanelGroup, PanelRow } from "../../components/panel";
import { StepMark } from "../../components/StepMark";
import { Motion, Presence } from "../../components/presence";
import { SaveDossierHint } from "../../components/SaveDossierHint";
import { Badge, Button, FloatingPanel, IconButton, Kbd, KbdHint, shortcut } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { k, t, tServer } from "../../i18n/i18n";
import { useDossierLanguage, useLineLabel } from "../../map/lineNames";
import { addLine, removableLine, removeLastLine, widthContextOf } from "../../map/manoeuvre/corridorEdit";
import { formatKm, formatKmBound } from "../../map/manoeuvre/corridorWidths";
import { STYLES } from "../../map/manoeuvre/zoMeasures";
import { entryZoom } from "../../map/recenter";
import { useManoeuvre } from "../../map/manoeuvre/useManoeuvre";
import { useManoeuvreWrites } from "../../map/manoeuvre/useManoeuvreWrites";
import { refuseWhileOrienting, useOrientation } from "../../map/manoeuvre/useOrientation";
import { attention } from "../../map/manoeuvre/useApplyOrientation";
import {
  CentacAxisNote,
  ORIENTATION_BLOCKS,
  OrientationCard,
  orientationBlock,
  OrientationRow,
} from "./OrientationControls";
import { useMeasureWrites } from "../../map/manoeuvre/useMeasureWrites";
import { FRIEND_BLUE, symbolSvg } from "../../map/units/symbols";
import { useDeployment } from "../../map/units/useDeployment";
import { placeableCount } from "../../map/units/eniUnits";
import { useUnitMoves } from "../../map/units/useUnitMoves";
import { flashLimaRow, LineStateBadge } from "../../panels/LineCard";
import { restoreGeneratedText } from "../../panels/MissionMenu";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { EniOdbUnits } from "../EniOdbUnits";
import { AddUnitButton, DossierUnits, EniGlobalLine, RemovedUnits, UnitBadges } from "../composition/DossierUnits";
import { UnitRowMenu } from "../composition/RoleMenu";
import { sideView, unitByKey } from "../composition/rows";
import { useUnitActions } from "../composition/useUnitActions";
import { UnitTimeline, useLocate } from "../MissionObjects";
import { OpenMissionDataButton } from "../openMissionData";
import { finishEditing, startEditing } from "./finishEditing";
import { NeutralConfirm } from "./NeutralConfirm";
import { CampRow, FreeZoneBuilder, RecreateZoneCallout, useAxisContext, ZoneCard } from "./ZoneStep";
import { TerrainWeather } from "./TerrainWeather";
import { designateZoneWait, refuseCorridorWhilePreparing } from "../../map/zones/zoneAttention";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { useOpenZone } from "../../map/zones/useZones";
import { useZoneDraw } from "../../map/zones/zoneDraw";
import { useFreeTerrainOffer } from "../../map/zones/zonesOpen";
import {
  corridorFindings,
  depthNote,
  legacyCorridor,
  limaRows,
  lineState,
  MEASURE_STATE_LABEL,
  measureAction,
  measureCenter,
  measureLabel,
  measureLevel,
  measureName,
  measurePosition,
  measureRows,
  measuresTouched,
  type StepState,
  stepStates,
  templateWidth,
  unitsOf,
  unitState,
  widthDetail,
  widthSummary,
  zoneDepths,
} from "./manoeuvreModel";
import { refuseIfDossierLocked } from "../../sharing/access";
import { Waiting } from "../../components/Waiting";

/** Vue « Manœuvre » (docs/14 §2.2) : panneau de second niveau sur le modèle Prevail (capture « Data connection ») —
 *  en-tête de 36 px, filet, puis cinq étapes (Zone, Fuseau, LIMA, Unités, Missions) en `PanelGroup` aux titres
 *  gris de poids moyen, actions à droite, lignes (`PanelRow`) à pastille d'exception et seconde ligne grise. Le fuseau s'édite sur la carte
 *  (`useManoeuvre.editing`, poignées et bandeau dessinés par la carte) ; LIMA, unités et missions se règlent
 *  ici ou sur la carte. Dossier non enregistré : étapes grisées, l'enregistrement est désigné. CENTAC : aucune
 *  étape, la rotation suit les limites du camp. */

// --- Briques du panneau -----------------------------------------------------------------------------------------

// Pastille d'étape : composant partagé avec les Données initiales (`components/StepMark`), sans numéro ici — coche bleu
// ami (faite), anneau animé doux (en cours), anneau gris (à faire).

/** Groupe d'étape : `PanelGroup` avec sa pastille d'étape devant le titre. */
function StepGroup({
  title,
  state,
  actions,
  children,
}: {
  title: string;
  state: StepState;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <PanelGroup title={title} leading={<StepMark state={state} />} actions={actions}>
      {children}
    </PanelGroup>
  );
}

/** Miniature du motif d'une ligne de la ZO (16 × 4 px, bleu ami) : trait plein, tireté (FLOT), pointillé (CFL),
 *  tiret-point (FSCL), comme sur la carte. */
function MeasureSwatch({ style, weight }: { style: MeasureStyle; weight: number }) {
  const dash = { solid: undefined, dashed: "5 2.5", dotted: "0 3", dashdot: "5 2 0 2" }[style];
  return (
    <svg width={16} height={4} viewBox="0 0 16 4" aria-hidden className="shrink-0">
      <line
        x1={style === "dotted" || style === "dashdot" ? 1 : 0}
        y1={2}
        x2={16}
        y2={2}
        stroke={FRIEND_BLUE}
        strokeWidth={weight >= 3 ? 2.5 : 1.75}
        strokeDasharray={dash}
        strokeLinecap={style === "dotted" || style === "dashdot" ? "round" : "butt"}
      />
    </svg>
  );
}

/** Groupe « Lignes et zones de la ZO » (docs/15 §4.5), entre les LIMA et les unités : une ligne par ligne de la ZO
 *  au temps affiché (motif, nom court dans la langue du dossier, règle qui la place ; état en pastille si elle est
 *  déplacée ou retirée, « Sans objet » en texte gris avec sa raison), « Centrer » et « Revenir à la position
 *  doctrinale » ; avertissements du serveur ; « Revenir aux positions doctrinales » (annulable, sans
 *  confirmation) ; profondeur des zones et note du niveau. Un clic sur une ligne ouvre sa fiche. Écritures
 *  marquées `data-write` : dossier non enregistré, le panneau désigne l'enregistrement. */
function MeasuresGroup({ pending }: { pending: boolean }) {
  const { data: deployment } = useDeployment();
  const phase = useUi((s) => s.phase);
  const selection = useUi((s) => s.selection);
  const select = useUi((s) => s.select);
  const flyTo = useUi((s) => s.requestFlyTo);
  const language = useDossierLanguage();
  const writes = useMeasureWrites();
  const measures = deployment?.corridor?.measures;
  const level = measureLevel(deployment?.level);
  if (!deployment || !measures || !level) return null;
  const busy = pending || writes.busy;
  // Dossier non enregistré : les écritures restent saisissables (le panneau désigne l'enregistrement), annoncées
  // indisponibles aux lecteurs d'écran, comme les autres écritures du panneau.
  const canEdit = writes.canEdit;
  const unitName = (key: string) => {
    const unit = deployment.units.find((u) => u.key === key);
    return unit ? unit.display_name || unit.designation : undefined;
  };
  const depths = zoneDepths(measures, level, language);

  return (
    <PanelGroup
      title={t("Lignes et zones de la ZO")}
      leading={
        <span className="flex size-4 shrink-0 items-center justify-center text-ink-muted">
          <Spline size={14} />
        </span>
      }
    >
      {measureRows(measures, level, phase).map((row) => {
        const center = row.line ? measureCenter(row.line) : null;
        const revert = measureAction(row, "revert", language);
        const label = measureLabel(row, language);
        const style = row.line ?? STYLES[row.key];
        return (
          <PanelRow
            key={row.key}
            data-measure-row={row.key}
            icon={<MeasureSwatch style={style.style} weight={style.weight} />}
            title={measureName(row.names, language).short}
            detail={measurePosition(row, measures, level, unitName)}
            state={
              row.state === "moved" || row.state === "off" ? (
                <Badge dot tone="accent">
                  {t(MEASURE_STATE_LABEL[row.state])}
                </Badge>
              ) : row.state === "absent" ? (
                <span className="text-meta whitespace-nowrap text-ink-muted">{t(MEASURE_STATE_LABEL.absent)}</span>
              ) : undefined
            }
            selected={selection?.layer === "measure" && selection.key === row.key}
            onClick={center ? () => select({ layer: "measure", key: row.key, phase: row.phase, lngLat: center }) : undefined}
            clickTitle={center ? t("Ouvrir la fiche de {line}", { line: label }) : undefined}
            actions={
              <>
                {center && (
                  <IconButton
                    size="xs"
                    title={t("Centrer")}
                    aria-label={t("Centrer sur {line}", { line: label })}
                    onClick={() => flyTo(center, entryZoom(deployment))}
                  >
                    <Crosshair size={14} />
                  </IconButton>
                )}
                {revert && (
                  <IconButton
                    data-write=""
                    size="xs"
                    disabled={canEdit && busy}
                    aria-disabled={canEdit ? undefined : true}
                    title={t("Revenir à la position doctrinale")}
                    aria-label={t("Revenir à la position doctrinale : {line}", { line: label })}
                    onClick={() => writes.setMeasure.mutate(revert)}
                  >
                    <RotateCcw size={14} />
                  </IconButton>
                )}
                {/* Sans retour possible, la place du second bouton reste : « Centrer » garde la colonne de celui des
                    LIMA, qui ont une corbeille à côté. */}
                {!revert && deployment.corridor?.model && <span aria-hidden className="size-6 shrink-0" />}
              </>
            }
          />
        );
      })}
      <Presence show={measures.warnings.length > 0}>
        <Motion origin="fade" className="space-y-px">
          {measures.warnings.map((warning) => (
            <PanelRow key={warning} icon={<TriangleAlert size={14} className="text-warn" />} title={tServer(warning)} />
          ))}
        </Motion>
      </Presence>
      <Presence show={measuresTouched(measures)}>
        <Motion origin="fade" className="px-2 py-2.5">
          <Button
            data-write=""
            variant="secondary"
            disabled={canEdit && busy}
            aria-disabled={canEdit ? undefined : true}
            aria-label={t("Revenir aux positions doctrinales : lignes et zones de la ZO")}
            onClick={() => writes.resetMeasures.mutate()}
          >
            <RotateCcw size={14} /> {t("Revenir aux positions doctrinales")}
          </Button>
        </Motion>
      </Presence>
      <div className="space-y-1 px-2 pt-1.5 pb-1 text-meta leading-snug text-ink-muted">
        {depths && <p>{depths}</p>}
        <p>{depthNote(level)}</p>
      </div>
    </PanelGroup>
  );
}

// --- Panneau -------------------------------------------------------------------------------------------------------

/** Pastille d'état d'une unité, réservée à l'exception (R6) : une unité à sa position doctrinale n'en a pas. */
const UNIT_STATE: Record<ReturnType<typeof unitState>, { tone: "accent" | "warn"; label: string } | null> = {
  doctrinal: null,
  anchored: { tone: "accent", label: k("Placée selon la mission") },
  moved: { tone: "warn", label: k("Déplacée") },
};

export function ManoeuvrePanel() {
  const params = useUi((s) => s.deploymentParams);
  const { data: deployment, error } = useDeployment();
  const { exercise } = useAxisContext();
  const { zone: openZone } = useOpenZone();
  const centac = params?.level === "CENTAC";
  // Règle d'offre unique (Données initiales, vue Manœuvre) : serveur ouvert, carte mondiale, hors CENTAC ; sinon ni
  // tracé ni création proposés.
  const { offered: freeOffered } = useFreeTerrainOffer(exercise?.parameters.level === "CENTAC");
  // Dossier encore sans carte (enregistré en « Terrain libre ») : le bloc de création est ouvert d'office, la
  // demande d'ouverture est prise ici (elle ne rouvrira pas le bloc plus tard, sur un camp).
  const startRequest = useZoneDraw((s) => s.startRequest);
  useEffect(() => {
    if (!params && startRequest) useZoneDraw.getState().consumeStart();
  }, [params, startRequest]);

  let body: ReactNode;
  if (!params) {
    // Pas encore de camp ni de zone : un camp se choisit dans les Données initiales, une zone libre se trace ici,
    // n'importe où sur le globe (docs/14 §2.7).
    const offerZone = freeOffered;
    body = (
      <>
        <EmptyState variant="inline" action={<OpenMissionDataButton />}>
          {offerZone && exercise
            ? // Dossier enregistré en « Terrain libre » : l'étape attendue est ici (parcours guidé).
              t("Cherchez un lieu, puis tracez l'axe du fuseau sur la carte.")
            : offerZone
              ? t("Choisissez un camp dans les Données initiales, ou préparez un terrain libre n'importe où sur le globe.")
              : t("Choisissez un camp, un niveau et une mission dans les Données initiales pour construire la manœuvre.")}
        </EmptyState>
        {offerZone ? (
          <StepGroup title={t("Terrain libre")} state="active">
            {exercise && <RecreateZoneCallout exercise={exercise} />}
            <FreeZoneBuilder />
          </StepGroup>
        ) : (
          // Zone libre fermée : une zone gardée dans le dossier (import, réception) reste signalée, sans action.
          exercise && <RecreateZoneCallout exercise={exercise} />
        )}
      </>
    );
  } else if (centac) {
    body = (
      <div className="flex items-start gap-2.5 px-4 py-4">
        <Info size={14} className="mt-0.5 shrink-0 text-ink-muted" />
        <div className="space-y-1.5 text-ui leading-relaxed">
          <p className="font-medium text-ink">{t("Les rotations CENTAC suivent les limites du camp de Mailly.")}</p>
          <p className="text-ink-muted">
            {t("Le fuseau, les LIMA et les missions sont ceux de la rotation ; les unités se déplacent sur la carte, pour tout l'OPO affiché.")}
          </p>
          <CentacAxisNote />
        </div>
      </div>
    );
  } else if (error) {
    // Zone libre refusée par le dispositif (prête mais sans terre exploitable, par exemple) : sa fiche reste affichée,
    // avec « Supprimer » désigné quand c'est l'action attendue.
    const noLand = zoneRefusalOf(error) === "ZONE_NO_LAND";
    body = (
      <>
        <div className="px-4 py-4">
          <Callout tone="danger">{error instanceof Error ? error.message : t("Dispositif indisponible.")}</Callout>
        </div>
        {openZone && (
          <StepGroup title={t("Terrain")} state="active">
            <ZoneCard zone={openZone} highlightRemove={noLand} />
          </StepGroup>
        )}
      </>
    );
  } else if (!deployment) {
    body = (
      <div className="flex items-center gap-2 px-4 py-4 text-ui text-ink-muted">
        <Spinner decorative /> {<Waiting>{t("Composition du dispositif…")}</Waiting>}
      </div>
    );
  } else {
    body = <Steps />;
  }

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.manoeuvre.icon}
      title={t("Manœuvre")}
      actions={<HelpButton screen="manoeuvre" buttonSize="sm" size={16} />}
    >
      {body}
    </FloatingPanel>
  );
}

function Steps() {
  const queryClient = useQueryClient();
  const params = useUi((s) => s.deploymentParams);
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const selection = useUi((s) => s.selection);
  const select = useUi((s) => s.select);
  const flyTo = useUi((s) => s.requestFlyTo);
  const notify = useUi((s) => s.notify);
  const { data: deployment } = useDeployment();
  const { data: maps = [] } = useQuery({ queryKey: ["maps"], queryFn: api.maps });
  const writes = useManoeuvreWrites();
  const moves = useUnitMoves();
  const editing = useManoeuvre((s) => s.editing);
  const working = useManoeuvre((s) => s.working);
  const orienting = useOrientation((s) => s.open);
  const draftOpen = useUi((s) => s.draft !== null);
  const selectedLine = useManoeuvre((s) => s.selectedLine);
  const keepWidth = useManoeuvre((s) => s.keepWidth);
  const setKeepWidth = useManoeuvre((s) => s.setKeepWidth);
  const lineLabel = useLineLabel();
  const locate = useLocate();
  const { exercise } = useAxisContext();
  const { zone } = useOpenZone();
  // « Zone libre… » demandé (Données initiales, ou bouton de l'étape Zone d'un camp) : le bloc de création s'ouvre.
  const startRequest = useZoneDraw((s) => s.startRequest);
  const zoneDrawing = useZoneDraw((s) => s.drawing?.mode === "axis");
  const pendingAxis = useZoneDraw((s) => s.axis !== null);
  // Même règle d'offre que la ligne du camp et les Données initiales (serveur ouvert, carte mondiale, hors CENTAC).
  const { offered: freeOffered } = useFreeTerrainOffer(params?.level === "CENTAC" || exercise?.parameters.level === "CENTAC");
  // Choix « Terrain libre » de ce dossier en attente de son terrain (Données initiales) : le bloc se rouvre avec la vue.
  const [builderOpen, setBuilderOpen] = useState(() => exerciseId !== null && useZoneDraw.getState().terrainFor === exerciseId);
  useEffect(() => {
    // Demande consommée une fois : une réouverture de la vue (ou un autre dossier) ne rouvre pas le bloc.
    if (startRequest && useZoneDraw.getState().consumeStart()) setBuilderOpen(true);
  }, [startRequest]);
  // Lieu nommé dans les Données initiales (le dossier attend son nouveau terrain) : le bloc de création est ouvert,
  // une fois par dossier et par lieu (« Garder le camp » le referme et retire le lieu).
  const namedPlace = exercise && exercise.parameters.level !== "CENTAC" ? (exercise.parameters.place ?? null) : null;
  const placeKey = namedPlace && exercise ? `${exercise.id}|${namedPlace.lon},${namedPlace.lat}` : null;
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!placeKey || openedFor.current === placeKey) return;
    openedFor.current = placeKey;
    setBuilderOpen(true);
  }, [placeKey]);
  const [confirmReset, setConfirmReset] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // Sous-listes repliables des unités et des missions (Amis ouverts, Ennemis repliés ; Ennemis ouverts d'emblée tant
  // que l'utilisateur n'a rien choisi et que des unités ennemies sont à placer : elles ne se cachent pas).
  const [open, setOpen] = useState({ unitsAMI: true, unitsODB: true, missionsAMI: true, missionsENI: false });
  const [eniUnitsOpen, setEniUnitsOpen] = useState<boolean | null>(null);
  const toggle = (key: keyof typeof open) => setOpen((o) => ({ ...o, [key]: !o[key] }));
  const unitActions = useUnitActions();
  const [unitMenu, setUnitMenu] = useState<string | null>(null);
  const saveHintRef = useRef<HTMLDivElement>(null);
  // Retouche de la carte affichée mais pas encore enregistrée : les écritures du panneau partiraient de la
  // géométrie d'avant le geste et l'écraseraient ; elles attendent la réponse.
  const gesturePending = working !== null;
  const eniToPlace = deployment?.composition?.eni.units.filter((u) => u.placeable).length ?? 0;
  const eniUnitsShown = eniUnitsOpen ?? eniToPlace > 0;
  if (!deployment) return null;

  const corridor = deployment.corridor;
  const model = corridor?.model ?? null;
  // Pendant un geste, la géométrie de travail (aperçu de la carte) : largeurs et écarts suivent en direct.
  const shown = (editing && working) || model;
  const canEdit = writes.canEdit;
  const steps = stepStates(deployment, { editing, selectedLine: Boolean(selectedLine) });
  const mapName = maps.find((m) => m.slug === params?.map_slug)?.name ?? deployment.map_slug;

  // --- Fuseau ---
  const findings = shown ? corridorFindings(shown, corridor?.seed_widths_m, widthContextOf(corridor, deployment.level_key)) : null;
  const summary = widthSummary(findings?.widths ?? corridor?.widths_m ?? []);
  const doctrinal = templateWidth(deployment);
  const widthText = summary
    ? summary.uniform
      ? formatKm(summary.min)
      : t("{min} à {max}", { min: formatKm(summary.min).replace(/\s?km$/, ""), max: formatKm(summary.max) })
    : "—";

  const corridorBusy = writes.busy || gesturePending;

  /** Dossier non enregistré : une action d'écriture désigne « Enregistrer » (lien qui clignote) et le dit. Les
   *  contrôles de lecture (déplier, localiser, centrer, ouvrir une fiche) restent actifs. */
  const designateSave = () => {
    if (refuseIfDossierLocked()) return; // lecture seule (docs/17 §3.2) : le bandeau, pas l'enregistrement
    flashAttention(saveHintRef.current?.querySelector<HTMLElement>("button") ?? null, "ring");
    notify({
      text: exerciseId ? t("Enregistrez les modifications du dossier pour construire la manœuvre.") : t("Créez le dossier pour construire la manœuvre."),
      tone: "info",
    });
  };
  const guardWrites = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target instanceof Element ? e.target.closest("[data-write], [aria-haspopup='menu']") : null;
    if (!target) return;
    if (canEdit) {
      // Mode orientation : toute autre écriture du dispositif (LIMA, lignes de la ZO, « Revenir au fuseau
      // automatique », unités) attend qu'il soit appliqué ou annulé ; l'aperçu partirait sinon d'un autre état.
      if (!orienting || target.closest("[data-orientation-card]")) return;
      e.preventDefault();
      e.stopPropagation();
      refuseWhileOrienting();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    designateSave();
  };

  // Zone libre pas encore utilisable : unités et missions attendent le terrain ; leur clic désigne l'action attendue
  // (carte de progression, « Réessayer », « Libérer de l'espace » ou « Supprimer » selon l'état de la zone).
  const terrainPending = Boolean(zone && zone.status !== "ready");
  const guardTerrain = (e: MouseEvent<HTMLDivElement>) => {
    if (!terrainPending || !zone) return;
    e.preventDefault();
    e.stopPropagation();
    designateZoneWait(zone, "terrain");
  };
  // Bloc de création ouvert sur un camp, ou sur un terrain libre quand « Terrain libre » a été choisi de nouveau
  // dans les Données initiales (le nouveau terrain remplacera l'actuel à sa création, qui referme le bloc).
  const showBuilder = freeOffered && (builderOpen || zoneDrawing || pendingAxis);

  const finish = () => {
    if (finishing) return;
    setFinishing(true);
    void finishEditing().finally(() => setFinishing(false));
  };

  // --- Sens de la manœuvre (docs/14 §2.14) ---
  const orientationOut = corridor?.orientation ?? null;
  const legacyBlocked = orientationOut?.reason === "legacy";
  // Exclusions : retouche du fuseau en cours, fuseau antérieur, graphique en cours de tracé ; l'action attendue est
  // désignée. Dossier non enregistré : `guardWrites` (bouton `data-write`).
  const block = orientationBlock({ canEdit, editing, legacy: legacyBlocked, draft: draftOpen });
  const orientBlocked = block !== null && block !== "save";
  const blockedOrientation = () => {
    if (!block || block === "save") return;
    const { attention: name, notice } = ORIENTATION_BLOCKS[block];
    notify({ text: t(notice), tone: "info" });
    designate(attention(name), block === "editing" ? "auto" : "ring");
  };
  const startOrientation = () => {
    if (orienting) return;
    if (useOrientation.getState().start(deployment)) {
      // Fuseau et LIMA masqués dans les calques : ils réapparaissent, la flèche se lit avec eux.
      const ui = useUi.getState();
      if (!ui.controlVisible.fuseau) ui.setControlVisible("fuseau", true);
    }
  };
  const blockedEditing = () => void refuseWhileOrienting();

  // Fuseau d'un terrain libre en préparation : il ne se retouche (« Modifier le fuseau », ajout, retrait, GDH saisi)
  // qu'une fois calé sur les routes, terrain prêt ; le clic désigne la carte de progression. Les LIMA glissent déjà.
  const corridorWaits = terrainPending;
  const blockedCorridor = () => (orienting ? blockedEditing() : void refuseCorridorWhilePreparing(zone));

  // --- LIMA ---
  const removable = model ? removableLine(model) : null;
  const addMissing = (name: PhaseLineName) => {
    if (refuseCorridorWhilePreparing(zone)) return;
    if (!model || corridorBusy) return;
    const next = addLine(model, name);
    if (next) writes.setCorridor.mutate({ corridor: next, label: t("ajout de {line}", { line: lineLabel(name) }) });
  };
  const removeLine = (name: PhaseLineName) => {
    if (refuseCorridorWhilePreparing(zone)) return;
    if (!model || corridorBusy) return;
    if (name !== removable) {
      notify({
        text:
          name === "L0" || removable === null
            ? t("{line} est la ligne de débouché citée par l'ordre", { line: lineLabel("L0") })
            : t("Retirez d'abord {line}", { line: lineLabel(removable) }),
        detail: t("Les LIMA se retirent en partant de la plus haute."),
        tone: "info",
      });
      flashLimaRow(removable);
      return;
    }
    const next = removeLastLine(model);
    if (!next) return;
    if (selection?.layer === "line" && selection.name === name) select(null);
    writes.setCorridor.mutate({ corridor: next, label: t("retrait de {line}", { line: lineLabel(name) }) });
  };

  // --- Unités et missions ---
  const friends = unitsOf(deployment.units, "AMI", phase);
  const enemies = unitsOf(deployment.units, "ENI", phase);
  // `DELETE /positions` rétablit tous les temps : le bouton vaut dès qu'une unité est déplacée à l'un d'eux.
  const anyMoved = deployment.units.some((u) => Object.values(u.positions).some((p) => p?.moved));
  const tasked = { AMI: unitsOf(deployment.units, "AMI"), ENI: unitsOf(deployment.units, "ENI") };
  const edited = deployment.missions_edited ?? [];

  // Composition du dossier (docs/18 §6.2) : menu de ligne (rôle, retrait) et listes « à placer » par camp.
  const composed = Boolean(deployment.composition);
  const unitRow = (u: DeployedUnit) => {
    const position = u.positions[phase];
    const state = UNIT_STATE[unitState(position)];
    const isSelected = selection?.layer === "unit" && selection.code === u.code;
    const view = u.side === "AMI" || u.side === "ENI" ? sideView(deployment, u.side) : null;
    const member = unitByKey(view, u.key);
    return (
      <PanelRow
        key={u.key}
        data-composition-unit={member?.id}
        icon={
          <span
            className="flex h-4 w-6 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
            dangerouslySetInnerHTML={{ __html: symbolSvg(u.sidc, 13) }}
          />
        }
        title={unitDesignation(u.display_name || u.designation)}
        state={
          <>
            {member && <UnitBadges unit={member} />}
            {state && (
              <Badge dot tone={state.tone}>
                {t(state.label)}
              </Badge>
            )}
          </>
        }
        selected={isSelected}
        onClick={() => locate(u, phase)}
        clickTitle={t("Localiser l'unité en {phase}", { phase })}
        actions={
          view && member ? (
            <UnitRowMenu
              view={view}
              unit={member}
              open={unitMenu === member.id}
              onOpenChange={(o) => setUnitMenu(o ? member.id : null)}
              busy={unitActions.writes.busy}
              draggable={false}
              onRole={(role) => unitActions.guard(() => unitActions.writes.setRole.mutate({ side: member.side, unit: member.id, role }))}
              onUnplace={() => unitActions.guard(() => unitActions.writes.unplace.mutate(member.key))}
              onRemove={() => unitActions.guard(() => unitActions.writes.compose.mutate({ op: "remove", side: member.side, key: member.id }))}
              onCommand={(command) =>
                unitActions.guard(() => unitActions.writes.compose.mutate({ op: "command", side: "AMI", key: member.id, command }))
              }
            />
          ) : undefined
        }
      />
    );
  };

  return (
    <>
      {/* Dossier non enregistré : le rappel partagé, en bandeau sous l'en-tête ; une action d'écriture le désigne. */}
      <Presence show={!canEdit}>
        <Motion origin="fade" ref={saveHintRef}>
          <SaveDossierHint action="construire la manœuvre" variant="banner" />
        </Motion>
      </Presence>
      <div onClickCapture={guardWrites} className={clsx("transition-opacity duration-(--gx-enter)", !canEdit && "opacity-40")}>
        {/* 1. Zone — la météo de l'exercice se règle ici, sous le climat du terrain libre ou sous la ligne du camp,
            sans revenir aux Données initiales (chantier de cohérence météo). Bloc de création d'un nouveau terrain
            ouvert : pas de réglage, la météo choisie sur le terrain qu'il remplacera serait perdue à sa création. */}
        <StepGroup title={t("Terrain")} state={terrainPending ? "active" : steps.zone}>
          {exercise && <RecreateZoneCallout exercise={exercise} />}
          {zone ? (
            <ZoneCard
              zone={zone}
              weather={
                <Presence show={!showBuilder}>
                  <Motion origin="fade">
                    <TerrainWeather zone={zone} mapSlug={params?.map_slug ?? null} canEdit={canEdit} onRefused={designateSave} />
                  </Motion>
                </Presence>
              }
            />
          ) : (
            <>
              <CampRow
                name={mapName}
                centac={params?.level === "CENTAC" || exercise?.parameters.level === "CENTAC"}
                builderShown={showBuilder}
                onFreeTerrain={() => setBuilderOpen(true)}
              />
              <Presence show={!showBuilder}>
                <Motion origin="fade">
                  {/* Pas de px-2 : le bouton de la ligne l'a déjà, comme la ligne du camp et celle du sens. */}
                  <TerrainWeather zone={null} mapSlug={params?.map_slug ?? null} canEdit={canEdit} onRefused={designateSave} className="pt-1 pb-2" />
                </Motion>
              </Presence>
            </>
          )}
          <OrientationRow deployment={deployment} onRedrawAxis={freeOffered ? () => setBuilderOpen(true) : undefined} />
          <Presence show={showBuilder}>
            <Motion origin="top">
              <FreeZoneBuilder onClose={() => setBuilderOpen(false)} keep={zone ? "terrain" : "camp"} />
            </Motion>
          </Presence>
          <div className="flex flex-wrap items-center gap-2 px-2 py-2.5">
            {/* « Modifier le fuseau » et « Terminé » se remplacent en fondu. */}
            <Motion key={editing ? "done" : "edit"} origin="fade">
              {editing ? (
                <Button data-attention="manoeuvre-done" onClick={finish} disabled={finishing}>
                  <Check size={14} /> {t("Terminé")}
                </Button>
              ) : (
                <Button
                  data-write=""
                  data-manoeuvre-edit=""
                  onClick={startEditing}
                  disabled={canEdit && !model}
                  blocked={canEdit && (orienting || corridorWaits)}
                  onBlocked={blockedCorridor}
                  aria-disabled={canEdit && !orienting && !corridorWaits ? undefined : true}
                >
                  <Route size={14} /> {t("Modifier le fuseau")}
                </Button>
              )}
            </Motion>
            {/* Sens de la manœuvre (docs/14 §2.14) : camp seulement (terrain libre : fixé par l'axe tracé). */}
            <Presence show={Boolean(orientationOut && orientationOut.reason !== "zone")}>
              <Motion origin="fade">
                <Button
                  data-write=""
                  variant="secondary"
                  onClick={startOrientation}
                  disabled={canEdit && (corridorBusy || orienting)}
                  blocked={canEdit && orientBlocked}
                  onBlocked={blockedOrientation}
                  aria-disabled={canEdit && !orientBlocked ? undefined : true}
                >
                  <Compass size={14} /> {t("Orienter la manœuvre")}
                </Button>
              </Motion>
            </Presence>
            <Presence show={Boolean(corridor?.custom)}>
              <Motion origin="fade">
                <Button
                  data-write=""
                  variant="ghost"
                  onClick={() => setConfirmReset(true)}
                  disabled={canEdit && corridorBusy}
                  aria-disabled={canEdit ? undefined : true}
                >
                  <RotateCcw size={14} /> {t("Revenir au fuseau automatique")}
                </Button>
              </Motion>
            </Presence>
          </div>
          <Presence show={orienting}>
            <Motion origin="top">
              <OrientationCard onFreeTerrain={() => setBuilderOpen(true)} freeOffered={freeOffered} />
            </Motion>
          </Presence>
        </StepGroup>

        {/* 2. Fuseau */}
        <StepGroup title={t("Fuseau")} state={steps.fuseau}>
          <PanelRow
            icon={<MoveHorizontal size={14} />}
            title={widthText}
            detail={widthDetail(doctrinal, corridor?.width_range_m ?? findings?.range, Boolean(findings && findings.modified.length === 0))}
            // Fuseau automatique : l'état normal, sans pastille ; seul un fuseau retouché en porte une.
            state={
              corridor?.custom ? (
                <Badge dot tone="accent">
                  {t("Modifié")}
                </Badge>
              ) : undefined
            }
          />
          {/* Alerte de largeur (docs/15 §2.4, §4.5) : pire paire et plage doctrinale de la clé de gabarit. */}
          {findings && findings.worstWidth !== null && findings.range ? (
            <PanelRow
              icon={<TriangleAlert size={14} className="text-warn" />}
              title={t("Hors de la plage doctrinale")}
              detail={t("{width} · plage {min} à {max} km", {
                width: formatKm(findings.worstWidth),
                min: formatKmBound(findings.range[0]),
                max: formatKmBound(findings.range[1]),
              })}
              state={
                <Badge dot tone="warn">
                  {plural(findings.alerts.length, t("paire"), t("paires"))}
                </Badge>
              }
            />
          ) : findings && findings.modified.length > 0 ? (
            <PanelRow
              icon={<Check size={14} />}
              title={plural(findings.modified.length, t("paire modifiée"), t("paires modifiées"))}
            />
          ) : null}
          {/* Fuseau tracé avant les largeurs doctrinales (docs/15 §2.4) : géométrie gardée, toutes ses paires
              comparées à la plage ; le fuseau automatique du camp est proposé. */}
          <Presence show={legacyCorridor(corridor)}>
            <Callout
              tone="warn"
              className="my-1"
              title={t("Fuseau tracé avant les largeurs doctrinales")}
              action={
                <Button
                  data-write=""
                  data-attention="manoeuvre-corridor-reset"
                  variant="link"
                  onClick={() => setConfirmReset(true)}
                  disabled={canEdit && corridorBusy}
                >
                  {t("Revenir au fuseau automatique")}
                </Button>
              }
            >
              {t("Toutes ses paires sont comparées à la plage doctrinale du niveau.")}
            </Callout>
          </Presence>
          {/* Même contrôle que dans le bandeau de la carte (préférence partagée). */}
          <div className="flex min-h-8 items-center gap-2 px-2 py-1.5">
            <Checkbox
              checked={keepWidth}
              onChange={setKeepWidth}
              title={t("Glisser un sommet déplace aussi son vis-à-vis : la largeur ne change pas")}
              label={t("Garder la largeur")}
              className="min-w-0 flex-1"
            />
            <KbdHint className="flex shrink-0 items-center gap-1 text-meta text-ink-muted">
              <Kbd>⌥</Kbd> {t("une seule limite")}
            </KbdHint>
          </div>
        </StepGroup>

        {/* 3. LIMA */}
        <StepGroup title={t("LIMA")} state={steps.lima}>
          <div className="space-y-1">
            {limaRows(deployment).map(({ name, present }) => {
              const line = corridor?.lines.find((l) => l.name === name);
              if (!present || !line) {
                return (
                  <Motion key={`add-${name}`} origin="fade">
                    <button
                      type="button"
                      data-write=""
                      disabled={canEdit && !corridorWaits && (!model || corridorBusy)}
                      aria-disabled={canEdit && !corridorWaits ? undefined : true}
                      onClick={() => addMissing(name)}
                      title={t("Ajouter {line}, placée entre ses voisines", { line: lineLabel(name) })}
                      className={clsx(
                        "flex min-h-8 w-full items-center gap-2.5 rounded-lg border border-dashed border-line-strong px-2 py-1.5 text-left text-ui font-medium text-ink-muted transition-colors hover:border-accent hover:bg-accent-soft/50 hover:text-accent disabled:opacity-40",
                        canEdit && corridorWaits && "opacity-40",
                      )}
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                        <Plus size={14} />
                      </span>
                      {lineLabel(name)}
                    </button>
                  </Motion>
                );
              }
              const state = lineState(model, name, line.moved);
              const hours = corridor?.effective_hours?.[name] ?? line.hours;
              const canRemove = name === removable && !corridorBusy;
              return (
                <Motion key={name} origin="fade">
                <PanelRow
                  data-lima-row={name}
                  icon={<span className="h-0.5 w-3.5 rounded-full" style={{ background: FRIEND_BLUE }} />}
                  title={lineLabel(name)}
                  detail={
                    <>
                      {/* Heures après le début de l'exercice : H est le franchissement de L0 (décision D10). */}
                      {`${t("Début")} + ${hours} h`}
                      {line.dtg && (
                        <>
                          {" · "}
                          <span className="whitespace-nowrap">{line.dtg}</span>
                        </>
                      )}
                    </>
                  }
                  state={<LineStateBadge entered={state === "entered"} moved={state === "moved"} />}
                  selected={selection?.layer === "line" && selection.name === name}
                  onClick={() => select({ layer: "line", name, lngLat: line.center })}
                  clickTitle={t("Ouvrir la fiche de {line}", { line: lineLabel(name) })}
                  actions={
                    <>
                      <IconButton size="xs" title={t("Centrer")} aria-label={t("Centrer sur {line}", { line: lineLabel(name) })} onClick={() => flyTo(line.center, 11.5)}>
                        <Crosshair size={14} />
                      </IconButton>
                      {model && (
                        // Seule la LIMA la plus haute se retire : sur une autre, le clic dit laquelle et la désigne.
                        <IconButton
                          data-write=""
                          size="xs"
                          blocked={!canRemove || corridorWaits}
                          onBlocked={() => removeLine(name)}
                          title={canRemove ? t("Retirer {line}", { line: lineLabel(name) }) : t("Seule la LIMA la plus haute se retire")}
                          aria-label={t("Retirer {line}", { line: lineLabel(name) })}
                          onClick={() => removeLine(name)}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      )}
                    </>
                  }
                />
                </Motion>
              );
            })}
          </div>
        </StepGroup>

        {/* Lignes et zones de la ZO (docs/15 §4.5) : hors des cinq étapes, sans pastille d'étape. */}
        <MeasuresGroup pending={corridorBusy} />

        {/* 4 et 5. Unités et missions : en zone libre, disponibles quand le terrain sera prêt. */}
        <Presence show={terrainPending}>
          <Motion origin="fade" className="px-4 pt-2 text-meta leading-snug text-ink-muted">
            {t("Unités et missions : disponibles quand le terrain sera prêt.")}
          </Motion>
        </Presence>
        <div
          onClickCapture={guardTerrain}
          className={clsx("transition-opacity duration-(--gx-enter)", terrainPending && "opacity-40")}
        >
          {/* 4. Unités */}
          <StepGroup title={t("Unités")} state={steps.unites}>
            <DisclosureRow icon={Users} title={t("Amis")} count={friends.length} open={open.unitsAMI} onToggle={() => toggle("unitsAMI")}>
              {friends.length ? friends.map(unitRow) : <p className="px-2 py-1.5 text-meta text-ink-muted">{t("Aucune unité amie à ce temps.")}</p>}
              {composed && <DossierUnits side="AMI" onLocate={locate} placeableOnly />}
              {composed && <RemovedUnits side="AMI" />}
              {composed && (
                <div className="px-2 py-2.5">
                  <AddUnitButton side="AMI" compact />
                </div>
              )}
            </DisclosureRow>
            <DisclosureRow
              icon={Swords}
              title={t("Ennemis")}
              count={enemies.length}
              open={eniUnitsShown}
              onToggle={() => setEniUnitsOpen(!eniUnitsShown)}
            >
              {enemies.length ? enemies.map(unitRow) : <p className="px-2 py-1.5 text-meta text-ink-muted">{t("Aucune unité ennemie à ce temps.")}</p>}
              {composed && <DossierUnits side="ENI" onLocate={locate} placeableOnly />}
              {composed && <EniGlobalLine />}
              {composed && <RemovedUnits side="ENI" />}
              {composed && (
                <div className="px-2 py-2.5">
                  <AddUnitButton side="ENI" compact />
                </div>
              )}
            </DisclosureRow>
            {/* Ordre de bataille ennemi du dossier (étape 3), serveur sans composition : unités sur la carte et unités à
                glisser depuis la liste. */}
            {deployment.eni_odb && !composed && (
              <DisclosureRow
                icon={Swords}
                title={t("Ordre de bataille ennemi")}
                count={placeableCount(deployment.eni_odb) || undefined}
                open={open.unitsODB}
                onToggle={() => toggle("unitsODB")}
              >
                <EniOdbUnits onLocate={locate} />
              </DisclosureRow>
            )}
            <div className="px-2 py-2.5">
              <Button
                data-write=""
                variant="secondary"
                disabled={canEdit && (!anyMoved || moves.busy)}
                aria-disabled={canEdit ? undefined : true}
                title={anyMoved ? undefined : t("Aucune unité déplacée")}
                onClick={() => moves.reset.mutate({})}
              >
                <RotateCcw size={14} /> {t("Revenir aux positions doctrinales")}
              </Button>
            </div>
          </StepGroup>

          {/* 5. Missions */}
          <StepGroup title={t("Missions")} state={steps.missions}>
            <Presence show={edited.length > 0 && Boolean(exerciseId)}>
              {exerciseId && (
                <Callout
                  tone="warn"
                  className="my-1"
                  action={
                    <Button data-write="" variant="link" onClick={() => void restoreGeneratedText(queryClient, exerciseId, edited)}>
                      {t("Rétablir le texte généré")}
                    </Button>
                  }
                >
                  {t("Le §3.b a été modifié à la main : les missions choisies n'y parviennent plus.")}
                </Callout>
              )}
            </Presence>
            <DisclosureRow icon={ListChecks} title={t("Amis")} count={tasked.AMI.length} open={open.missionsAMI} onToggle={() => toggle("missionsAMI")}>
              <ul className="space-y-0.5 pb-1">
                {tasked.AMI.map((u) => (
                  <UnitTimeline key={u.key} unit={u} phase={phase} onLocate={(p) => locate(u, p)} thumbnails />
                ))}
              </ul>
            </DisclosureRow>
            <DisclosureRow icon={ListChecks} title={t("Ennemis")} count={tasked.ENI.length} open={open.missionsENI} onToggle={() => toggle("missionsENI")}>
              <ul className="space-y-0.5 pb-1">
                {tasked.ENI.map((u) => (
                  <UnitTimeline key={u.key} unit={u} phase={phase} onLocate={(p) => locate(u, p)} thumbnails />
                ))}
              </ul>
            </DisclosureRow>
            <p className="px-2 pt-1.5 pb-1 text-meta leading-snug text-ink-muted">
              {t("Si le 1er échelon change de posture, les autres unités, l'ennemi et l'ordre s'y accordent ; vos choix restent.")}
            </p>
          </StepGroup>
        </div>
      </div>

      <Presence show={confirmReset}>
        {confirmReset && (
          <NeutralConfirm
            title={t("Revenir au fuseau automatique ?")}
            confirmLabel={t("Revenir au fuseau automatique")}
            onConfirm={() => {
              setConfirmReset(false);
              writes.resetCorridor.mutate();
            }}
            onCancel={() => setConfirmReset(false)}
          >
            {t("Le fuseau retouché est remplacé par le fuseau automatique du camp, et les LIMA reprennent leur place d'avant la retouche. {touche} annule ce retour.", { touche: shortcut("Z") })}
          </NeutralConfirm>
        )}
      </Presence>
    </>
  );
}
